/**
 * Editor Session State
 *
 * Lightweight state container that tracks the publish lifecycle for an editor
 * session. Manages source asset identity, content snapshots for dirty detection,
 * undo-depth-aware publish tracking, and concurrent-publish guards.
 *
 * Framework-agnostic: does not own the undo manager or perform any I/O.
 * The consuming editor injects an undo depth getter and wires up change
 * notifications to its own UI.
 */

/** Minimal asset identity needed for session tracking. */
export interface SessionAsset {
    assetId: string;
    title?: string | null;
    teamId?: string;
}

/** Read-only snapshot of the current session state. */
export interface SessionState {
    sourceAsset: SessionAsset | null;
    contentSnapshot: string | null;
    publishDepth: number;
    isPublishing: boolean;
    isDirty: boolean;
}

export type SessionChangeCallback = (state: SessionState) => void;

export class EditorSession {
    private _sourceAsset: SessionAsset | null = null;
    private _contentSnapshot: string | null = null;
    private _publishDepth = 0;
    private _isPublishing = false;
    private _getUndoDepth: () => number;
    private _listeners: Set<SessionChangeCallback> = new Set();

    /**
     * @param getUndoDepth Function returning the current undo stack depth.
     *   EditorSession compares this against the depth at last publish to
     *   determine `isDirty`. The undo manager itself stays external.
     */
    constructor(getUndoDepth: () => number) {
        this._getUndoDepth = getUndoDepth;
    }

    // ── Accessors ───────────────────────────────────────────────────────

    /** The asset this session was opened from (null if standalone/disconnected). */
    get sourceAsset(): SessionAsset | null { return this._sourceAsset; }

    /** Serialized export data snapshot from open/last-publish time. */
    get contentSnapshot(): string | null { return this._contentSnapshot; }

    /** Whether a publish is currently in progress. */
    get isPublishing(): boolean { return this._isPublishing; }

    /** Whether the user has unsaved changes since the last publish/open. */
    get isDirty(): boolean {
        if (!this._sourceAsset) return false;
        return this._getUndoDepth() !== this._publishDepth;
    }

    // ── Lifecycle ───────────────────────────────────────────────────────

    /**
     * Called when an asset is opened from a CMS/catalog.
     * Sets the source asset identity and captures the initial content snapshot.
     */
    open(asset: SessionAsset, contentSnapshot: string | null): void {
        this._sourceAsset = asset;
        this._contentSnapshot = contentSnapshot;
        this._publishDepth = this._getUndoDepth();
        this._isPublishing = false;
        this._notify();
    }

    /**
     * Called after a successful publish. Updates the content snapshot and
     * records the current undo depth as the new "clean" baseline.
     */
    markPublished(newContentSnapshot?: string): void {
        if (newContentSnapshot !== undefined) {
            this._contentSnapshot = newContentSnapshot;
        }
        this._publishDepth = this._getUndoDepth();
        this._notify();
    }

    /**
     * Acquire the publish lock. Returns `false` if a publish is already in
     * progress (preventing concurrent publishes).
     */
    beginPublish(): boolean {
        if (this._isPublishing) return false;
        this._isPublishing = true;
        this._notify();
        return true;
    }

    /** Release the publish lock after publish completes (success or failure). */
    endPublish(): void {
        this._isPublishing = false;
        this._notify();
    }

    /** Reset to disconnected state. Called when switching scenes or closing. */
    disconnect(): void {
        this._sourceAsset = null;
        this._contentSnapshot = null;
        this._publishDepth = 0;
        this._isPublishing = false;
        this._notify();
    }

    /**
     * Called when the undo stack shifts (an undo command is dropped from the
     * bottom of the stack). This keeps `_publishDepth` in sync so that
     * `isDirty` remains correct even as the stack compacts.
     *
     * Wire this to your undo manager's `depthShifted` callback.
     */
    onUndoDepthShifted(): void {
        if (this._publishDepth > 0) {
            this._publishDepth--;
        } else {
            // Publish point is no longer reachable via undo — permanently dirty
            this._publishDepth = -1;
        }
        this._notify();
    }

    // ── Change notification ─────────────────────────────────────────────

    /** Subscribe to state changes. Returns an unsubscribe function. */
    subscribe(callback: SessionChangeCallback): () => void {
        this._listeners.add(callback);
        return () => { this._listeners.delete(callback); };
    }

    /** Get a read-only snapshot of the current state. */
    getState(): SessionState {
        return {
            sourceAsset: this._sourceAsset,
            contentSnapshot: this._contentSnapshot,
            publishDepth: this._publishDepth,
            isPublishing: this._isPublishing,
            isDirty: this.isDirty,
        };
    }

    // ── Private ─────────────────────────────────────────────────────────

    private _notify(): void {
        const state = this.getState();
        for (const listener of this._listeners) {
            listener(state);
        }
    }
}
